import { Button, H2, H3, H4, H5, IconPrimary, Li, P, Pagination, Space, Span, Ul } from '@dnb/eufemia';
import type { SpacingElementProps } from '@dnb/eufemia/shared/types';
import { TrackRoute } from '@portals/shared-frontend/components';
import Markdown from '@portals/shared-frontend/components/Markdown';
import classnames from 'classnames';
import { format } from 'date-fns';
import { type JSX, useState } from 'react';
import { Link } from 'react-router-dom';

import HeroPage from '@/components/HeroPage';
import { HeroContent } from '@/components/HeroPage/HeroContent';
import StatusWidget from '@/components/StatusWidget';
import { useBackend } from '@/hooks/useBackend';
import useReturnTo from '@/hooks/useReturnTo';
import type { ApiIncidentResponse, ApiStatusSummaryResponse } from '@/openapi/schema';
import { highestSeverity, SEVERITY_CLASS_SUFFIX, SEVERITY_LABEL } from '@/utils/apiStatusSeverity';

import style from './Status.module.css';

/**
 * Dummy data for skeletons
 */

const DUMMY_STATUS = new Array(5).fill(0).map<ApiStatusSummaryResponse>((_, i) => ({
  apiSlug: `dummy-api-${i}`,
  apiName: '',
  severity: 'OK',
  ongoingCount: 0,
  upcomingCount: 0,
  pastCount: 0,
}));

/**
 * Helpers
 */

function formatDateRange(start: string, end: string) {
  return `${formatDate(start)} – ${formatDate(end)}`;
}

function formatDate(date: string) {
  return format(new Date(date), 'MMMM d, yyyy, HH:mm, O');
}

/**
 * Incident card
 */

interface IncidentCardProps extends SpacingElementProps {
  incident: ApiIncidentResponse;
}

function IncidentCard({ incident, ...props }: IncidentCardProps): JSX.Element {
  const title =
    incident.type === 'maintenance' && incident.resolvedAt
      ? formatDateRange(incident.reportedAt, incident.resolvedAt)
      : formatDate(incident.reportedAt);

  const maintenanceCompleted =
    incident.type === 'maintenance' && incident.resolvedAt && new Date(incident.resolvedAt).valueOf() < Date.now();

  return (
    <Space {...props} className={style['IncidentCard']}>
      <H4>{title}</H4>
      <P size="small" top="x-small">
        ID: {incident.id}
      </P>
      {incident.description && <Markdown>{incident.description}</Markdown>}
      {incident.updates.length > 0 && (
        <Ul inside top="small">
          {incident.updates.map((update) => (
            <Li key={update.id}>
              <H5>{formatDate(update.reportedAt)}</H5>
              {update.description && <Markdown>{update.description}</Markdown>}
            </Li>
          ))}
        </Ul>
      )}
      {incident.resolvedAt && incident.type !== 'maintenance' && (
        <Space className={style['IncidentCard-resolved']} top="small">
          <IconPrimary icon="check" right="x-small" />
          Resolved {formatDate(incident.resolvedAt)}
        </Space>
      )}
      {maintenanceCompleted && (
        <Space className={style['IncidentCard-resolved']} top="small">
          <IconPrimary icon="check" right="x-small" />
          Maintenance completed
        </Space>
      )}
    </Space>
  );
}

/**
 * Status card
 */

const INCIDENTS_PER_PAGE = 10;

interface StatusCardProps extends SpacingElementProps {
  status: ApiStatusSummaryResponse;
}

function StatusCard({ status, ...props }: StatusCardProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(1);

  // Incidents are only fetched once the card is opened. The overview is rendered from the counts in
  // the summary, so a collapsed card costs no request. keepPreviousData holds the current page in
  // place while the next one loads, so the pagination control does not unmount under the cursor.
  const { data: incidents } = useBackend(
    '/api-status/{apiSlug}/incidents',
    expanded ? { params: { path: { apiSlug: status.apiSlug }, query: { page: page - 1 } } } : null,
    { keepPreviousData: true },
  );

  const numberOfPages = Math.ceil(status.pastCount / INCIDENTS_PER_PAGE);

  const contentId = `status-card-content-${status.apiSlug}`;

  const hasIncidents = status.ongoingCount > 0 || status.upcomingCount > 0 || status.pastCount > 0;

  return (
    <Space
      className={classnames({
        [style['StatusCard']!]: true,
        [style['StatusCard--expanded']!]: expanded,
        [style[`StatusCard--severity-${SEVERITY_CLASS_SUFFIX[status.severity]}`]!]: true,
      })}
      {...props}
    >
      <button
        aria-controls={contentId}
        aria-expanded={expanded}
        className={classnames([style['StatusCard-button'], 'dnb-tab-focus'])}
        onClick={() => setExpanded(!expanded)}
        type="button"
      >
        <IconPrimary icon="chevron_down" size="medium" />
        <H2 size="medium">{status.apiName}</H2>
        <Span className={style['StatusCard-severity']}>{SEVERITY_LABEL[status.severity]}</Span>
      </button>

      <div className={style['StatusCard-content']} id={contentId}>
        {hasIncidents && !incidents && (
          <P top="large">
            <i>Loading incidents…</i>
          </P>
        )}

        {incidents && incidents.ongoing.length > 0 && (
          <>
            <H3 className={style['StatusCard-ongoing']} top="large">
              Ongoing
            </H3>
            {incidents.ongoing.map((incident) => (
              <IncidentCard incident={incident} key={incident.id} top="small" />
            ))}
          </>
        )}
        {incidents && incidents.upcoming.length > 0 && (
          <>
            <H3 top="large">Upcoming maintenance</H3>
            {incidents.upcoming.map((incident) => (
              <IncidentCard incident={incident} key={incident.id} top="small" />
            ))}
          </>
        )}

        {incidents && status.pastCount > 0 && (
          <Pagination currentPage={page} onChange={({ pageNumber }) => setPage(pageNumber)} pageCount={numberOfPages}>
            <>
              <H3 top="large">Past incidents</H3>
              {incidents.past.content.map((incident) => (
                <IncidentCard incident={incident} key={incident.id} top="small" />
              ))}
            </>
          </Pagination>
        )}

        {!hasIncidents && (
          <P top="large">
            <i>No incidents</i>
          </P>
        )}
      </div>
    </Space>
  );
}

/**
 * Status page
 */

const WIDGET_TEXT = {
  OK: 'All APIs are operating normally',
  INSTABILITIES: 'We are looking into it. Please see below for updates.',
  REDUCED_SERVICE: 'We are looking into it. Please see below for updates.',
};

export default function Status(): JSX.Element {
  const { data: status, isLoading } = useBackend('/api-status', {});
  const [, setReturnTo] = useReturnTo();

  const severity = highestSeverity((status ?? []).map(({ severity }) => severity));

  const hasLoaded = !isLoading && !!status;

  return (
    <HeroPage
      banner="status"
      skeleton={!hasLoaded}
      subtitle="Status page for the DNB APIs. Here you will find an up-to-date status on the services and information about ongoing incidents."
      tabTitle="API Status - Overview"
      title="API Status - Overview"
    >
      <HeroContent>
        <TrackRoute name="API Status" path="/status" type="generic" />
        {severity === 'OK' ? (
          <P className={style['Status-operational']} size="medium" weight="medium">
            All APIs are operating normally
          </P>
        ) : (
          <StatusWidget content={WIDGET_TEXT} severity={severity} />
        )}

        <Space bottom="large" top="large">
          <Button element={Link} size="large" to="/subscriptions/status">
            Subscribe
          </Button>
          <Button
            element={Link}
            left="x-small"
            onClick={() => {
              setReturnTo('/status');
            }}
            size="large"
            to="/unsubscribe"
            variant="secondary"
          >
            Unsubscribe
          </Button>
        </Space>

        {(hasLoaded ? status : DUMMY_STATUS).map((status) => (
          <StatusCard key={status.apiSlug} status={status} top="small" />
        ))}
      </HeroContent>
    </HeroPage>
  );
}
